import { computed, inject, Injectable, signal } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import {
  FormFieldConfig,
  FormSection,
  FormWizardConfig,
  FormWizardSectionState,
} from './models/form-wizard.models';

@Injectable()
export class FormWizardStateService<T = any> {
  private readonly fb = inject(FormBuilder);
  private readonly sectionStatesSignal = signal<FormWizardSectionState[]>([]);

  readonly sectionsState = computed(() => this.sectionStatesSignal());
  readonly overallProgress = computed(() => this.calculateOverallProgress());

  form!: FormGroup;
  visibleSections: FormSection<T>[] = [];

  initialize(config: FormWizardConfig<T>, initialData?: T): void {
    this.visibleSections = [...(config.sections || [])];
    this.form = this.buildForm();

    if (initialData) {
      this.loadInitialData(initialData);
    }

    this.initializeSectionStates();
    this.updateSectionStates();
  }

  getAllFields(): FormFieldConfig<T>[] {
    return this.visibleSections.flatMap((section) => section.fields || []);
  }

  getSectionState(index: number): FormWizardSectionState {
    return (
      this.sectionsState()[index] || {
        index,
        collapsed: false,
        isValid: false,
        isTouched: false,
        fieldCount: 0,
        validFieldCount: 0,
        requiredFieldCount: 0,
        hasErrors: false,
      }
    );
  }

  expandSection(index: number): void {
    const states = [...this.sectionsState()];
    if (!states[index] || !states[index].collapsed) {
      return;
    }

    states[index] = {
      ...states[index],
      collapsed: false,
    };
    this.sectionStatesSignal.set(states);
  }

  toggleSection(index: number): void {
    const states = [...this.sectionsState()];
    if (!states[index]) {
      return;
    }

    states[index] = {
      ...states[index],
      collapsed: !states[index].collapsed,
    };
    this.sectionStatesSignal.set(states);
  }

  expandInvalidSections(): void {
    const states = this.sectionsState().map((state) =>
      state.hasErrors ? { ...state, collapsed: false } : state
    );

    this.sectionStatesSignal.set(states);
  }

  setFormData(data: Partial<T>): void {
    if (!this.form || !data) {
      return;
    }

    this.form.patchValue(data);
    this.updateSectionStates();
  }

  resetToInitial(initialData?: T): void {
    if (!this.form) {
      return;
    }

    const resetState = this.getAllFields().reduce((acc, field) => {
      acc[field.key] = this.getDefaultValue(field);
      return acc;
    }, {} as Record<string, unknown>);

    this.form.reset(resetState);

    if (initialData) {
      this.loadInitialData(initialData);
    }

    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.updateSectionStates();
  }

  updateSectionStates(): void {
    if (!this.form) {
      return;
    }

    const states = this.visibleSections.map((section, index) => {
      const fields = section.fields || [];
      let validFieldCount = 0;
      let isTouched = false;

      fields.forEach((field) => {
        const control = this.form.get(field.key);
        if (!control) {
          return;
        }

        if (control.valid) {
          validFieldCount++;
        }

        if (control.touched) {
          isTouched = true;
        }
      });

      const isValid = fields.every((field) => this.form.get(field.key)?.valid ?? true);
      const currentState = this.getSectionState(index);

      return {
        index,
        collapsed: currentState.collapsed,
        isValid,
        isTouched,
        fieldCount: fields.length,
        validFieldCount,
        requiredFieldCount: fields.filter((field) => field.required).length,
        hasErrors: !isValid && isTouched,
      };
    });

    this.sectionStatesSignal.set(states);
  }

  private buildForm(): FormGroup {
    const formControls = this.getAllFields().reduce<Record<string, any>>((acc, field) => {
      acc[field.key] = this.fb.control(
        {
          value: this.getDefaultValue(field),
          disabled: field.disabled || false,
        },
        this.buildValidators(field)
      );

      return acc;
    }, {});

    return this.fb.group(formControls);
  }

  private initializeSectionStates(): void {
    const states = this.visibleSections.map((section, index) => ({
      index,
      collapsed: section.collapsed !== false && section.collapsible === true,
      isValid: false,
      isTouched: false,
      fieldCount: section.fields.length,
      validFieldCount: 0,
      requiredFieldCount: section.fields.filter((field) => field.required).length,
      hasErrors: false,
    }));

    this.sectionStatesSignal.set(states);
  }

  private loadInitialData(initialData: T): void {
    const processedData = { ...(initialData as Record<string, unknown>) };

    this.getAllFields().forEach((field) => {
      if (field.type === 'date' && processedData[field.key]) {
        processedData[field.key] = this.getDateValue(processedData[field.key]);
      }
    });

    this.form.patchValue(processedData);
  }

  private buildValidators(field: FormFieldConfig<T>): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (field.required) {
      validators.push(Validators.required);
    }

    if (field.type === 'email') {
      validators.push(Validators.email);
    }

    if (field.type === 'url') {
      validators.push(Validators.pattern(/^https?:\/\/.+/));
    }

    field.validations?.forEach((rule) => {
      switch (rule.type) {
        case 'required':
          validators.push(Validators.required);
          break;
        case 'min':
          if (rule.value !== undefined) {
            validators.push(Validators.min(rule.value));
          }
          break;
        case 'max':
          if (rule.value !== undefined) {
            validators.push(Validators.max(rule.value));
          }
          break;
        case 'minLength':
          if (rule.value !== undefined) {
            validators.push(Validators.minLength(rule.value));
          }
          break;
        case 'maxLength':
          if (rule.value !== undefined) {
            validators.push(Validators.maxLength(rule.value));
          }
          break;
        case 'pattern':
          if (rule.value) {
            validators.push(Validators.pattern(rule.value));
          }
          break;
        case 'custom':
          if (rule.validator) {
            validators.push(rule.validator);
          }
          break;
      }
    });

    return validators;
  }

  private getDefaultValue(field: FormFieldConfig<T>): any {
    if (field.defaultValue !== undefined) {
      return field.defaultValue;
    }

    if (field.type === 'switch') {
      return false;
    }

    return null;
  }

  private getDateValue(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  private calculateOverallProgress(): number {
    const states = this.sectionsState();
    if (states.length === 0) {
      return 0;
    }

    const totalFields = states.reduce(
      (sum: number, state: FormWizardSectionState) => sum + state.fieldCount,
      0
    );
    const validFields = states.reduce(
      (sum: number, state: FormWizardSectionState) => sum + state.validFieldCount,
      0
    );

    return totalFields > 0 ? Math.round((validFields / totalFields) * 100) : 0;
  }
}
